// Record pages: the person page on RecordPage (record-page redesign step 13;
// docs/record-page-redesign.md "### Contact · person", "### Programs",
// "### Outreach history", build plan 13). Rendered by ContactDetail
// (views/contact_detail.jsx) on a desktop or iPad when the team's record layout
// is new (Settings → Feature flags; an admin's ⌘K preview); phones are untouched.
//
// ContactDetail keeps every hook, fetch and save path and hands them down in
// one props bag; this file owns the v2-only hooks (the summary, the draft
// flags, usePanelParam) and renders <window.RecordPage>. Cross-file names are
// read from window at render time, never at module evaluation. The pure
// helpers are window.ContactPageKit (test/contact_page_v2.test.mjs).

const core = () => window.RecordPanelsCore;

// ── ContactPageKit (pure; node-tested) ────────────────────────────────────────
// = contact_detail.jsx REFERRAL_MAKER_ROLES (pinned equal by the python test).
const MAKER_ROLES = ['referral_partner', 'referral_source', 'gpp'];
// The classic ⋯ create keys → the Programs drawer's ?tab=.
const PROGRAM_TAB = { referral: 'referral', event: 'events', gpp: 'gpp', bb: 'brand' };
const PROGRAM_TABS = ['referral', 'made', 'events', 'gpp', 'brand'];
const FOLD_EMAIL_BELOW = 1180;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// 'Mar 2026' on the UTC calendar day (referral_date is midnight UTC; see fmtReferralDay).
const monYear = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};
// Every number and address on the record as rows. A number parsePhones can't
// dial ('. (Work)') is kept as one dimmed, undialable row.
const reach = (raw, kit = window.AtlasPhone) => {
  const r = raw || {};
  const parsed = r.phone && kit && kit.parsePhones ? kit.parsePhones(r.phone) : [];
  const phones = parsed.map((p) => ({ key: p.tel, display: p.display, label: p.label || null, tel: p.tel, sms: p.sms, dialable: true }));
  if (!phones.length && String(r.phone || '').trim()) {
    phones.push({ key: 'raw', display: String(r.phone).trim(), label: null, tel: null, sms: null, dialable: false });
  }
  const emails = kit && kit.contactEmails ? kit.contactEmails(r) : [];
  return { phones, emails, dialable: phones.filter((p) => p.dialable) };
};
// What row 1 shows per control: 'split' / 'icon' / null. Email folds out of the
// header under a 1180px body and on touch; Call goes icon-only there.
const headerBudget = (bodyW, o) => {
  const x = o || {};
  const narrow = !!x.touch || (Number.isFinite(bodyW) && bodyW < FOLD_EMAIL_BELOW);
  return {
    logReferral: !!x.canWrite && !!x.referralRole && !x.merged,
    call: (x.phones || 0) > 0 ? (narrow ? 'icon' : 'split') : null,
    email: (x.emails || 0) > 0 && !narrow ? 'split' : null,
  };
};
// One line per program that applies (summary.programs + roles); [] = the card hides.
const programLines = (programs, roles, o) => {
  const p = programs || {};
  const rs = Array.isArray(roles) ? roles : [];
  const x = o || {};
  const out = [];
  if (p.referral || rs.includes('referred')) {
    out.push({ tab: 'referral', text: p.referral
      ? `Referred by ${p.referral.source_name || '—'}${p.referral.date ? ' · ' + monYear(p.referral.date) : ''}`
      : 'Referred by —' });
  }
  if (rs.some((r) => MAKER_ROLES.includes(r))) out.push({ tab: 'made', text: `Referrals they made · ${x.made == null ? '—' : x.made}` });
  if (p.gpp || rs.includes('gpp')) {
    out.push({ tab: 'gpp', text: p.gpp ? `GPP · ${p.gpp.stage || '—'} · ${p.gpp.referrals_sent || 0} referrals sent` : 'GPP · no partner record' });
  }
  if (p.brand_builder || rs.includes('referral_partner')) {
    out.push({ tab: 'brand', text: p.brand_builder
      ? (p.brand_builder.status === 'active' ? `Brand Builder · since ${monYear(p.brand_builder.since)}` : 'Brand Builder · removed')
      : 'Brand Builder · —' });
  }
  if (p.events && p.events.count > 0) out.push({ tab: 'events', text: `Events · ${p.events.attended || 0} attended` });
  return out;
};
// = contact_detail.jsx OUTREACH_STATUS_HUE (private there).
const OUTREACH_HUE = {
  responded: 'amber', call_scheduled: 'green', ds_call_held: 'green',
  not_interested: 'rose', too_small: 'slate', no_show: 'rose', future: 'blue',
  bad_contact: 'rose', unsubscribed: 'rose',
};
// The Outreach card's one line; null = the card hides (and ⋯ offers Add to PSAC).
const outreachLine = (o) => {
  if (!o || (o.status == null && !(o.campaigns > 0) && o.psac == null)) return null;
  const st = o.status || null;
  return {
    status: st && st.label ? st.label : null,
    hue: st ? (OUTREACH_HUE[st.status] || 'slate') : 'slate',
    channel: st && st.channel ? (st.channel === 'call' ? 'cold call' : st.channel) : null,
    campaign: o.last_campaign ? o.last_campaign.name : null,
    reply: o.last_reply ? [o.last_reply.interest || null, o.last_reply.at ? monYear(o.last_reply.at) : null].filter(Boolean).join(' · ') : null,
    psac: o.psac || null,
    campaigns: o.campaigns || 0,
  };
};
// The company's engagements for the Deals card: live / done (archived or exited)
// / pursuit shells; `count` excludes the shells (as the classic KPI did).
const dealsSplit = (engs) => {
  const all = Array.isArray(engs) ? engs : [];
  const deals = all.filter((e) => e.type !== 'buyer_buyside');
  return {
    live: deals.filter((e) => !e.archived && !e.exitName),
    done: deals.filter((e) => e.archived || e.exitName),
    shells: all.filter((e) => e.type === 'buyer_buyside'),
    count: deals.length,
  };
};
// The Reach block's flag chips from summary.calls.
const callFlags = (c) => (!c ? [] : [
  c.bad_phone && 'Phone marked bad',
  c.queued && 'Marked to call',
  c.sent_at && `In PhoneBurner since ${window.fmtDate ? window.fmtDate(String(c.sent_at).slice(0, 10)) : String(c.sent_at).slice(0, 10)}`,
].filter(Boolean));
// The Reach fold's preview: '1 phone · 2 emails'.
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const reachPreview = (r) => `${plural(r.phones.length, 'phone')} · ${plural(r.emails.length, 'email')}`;
// The Programs drawer's tabs: the programs that apply plus the one asked for (a create flow).
const programTabs = (programs, roles, requested) => {
  const have = new Set(programLines(programs, roles, {}).map((l) => l.tab));
  if (requested && PROGRAM_TABS.includes(requested)) have.add(requested);
  return PROGRAM_TABS.filter((t) => have.has(t));
};
// The RoleChips tooltips on v2: the classic map (contactRoleDetails) with its
// panel pointers re-aimed at the Programs drawer, plus the imported contact
// type (All details' "Imported type") when it is set and not the bulk default.
const PANEL_WORDING = [
  ['Details in the Referral panel.', 'Details in Programs.'],
  ['See the panel below.', 'See Programs.'],
  ['see the GPP panel below.', 'see Programs.'],
];
const IMPORTED_TYPE_DEFAULT = 'Seller: Primary Contact';
const roleTips = (raw, base) => {
  const r = raw || {};
  const src = base || {};
  const imported = r.dc_contact_type && r.dc_contact_type !== IMPORTED_TYPE_DEFAULT ? String(r.dc_contact_type) : null;
  const keys = new Set([...Object.keys(src), ...(Array.isArray(r.roles) ? r.roles : [])]);
  const out = {};
  keys.forEach((k) => {
    let t = typeof src[k] === 'string' ? src[k] : '';
    PANEL_WORDING.forEach(([from, to]) => { t = t.split(from).join(to); });
    if (imported) t = t ? `${t} Imported type: ${imported}.` : `Imported type: ${imported}`;
    if (t) out[k] = t;
  });
  return out;
};
window.ContactPageKit = Object.freeze({
  MAKER_ROLES, PROGRAM_TAB, PROGRAM_TABS, FOLD_EMAIL_BELOW, reach, headerBudget, programLines, outreachLine,
  dealsSplit, callFlags, monYear, programTabs, roleTips, reachPreview,
});

// ── row + styles ─────────────────────────────────────────────────────────────
const V2_LINK = { background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--accent-text)',
  fontSize: 12, fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 };
const V2_DIM = { color: 'var(--text-4)', fontSize: 11 };
const V2_ROW = { display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0, flexWrap: 'wrap' };
const V2_H1 = { fontSize: 16, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.01em', margin: 0,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const V2_PILL_BTN = { background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', fontFamily: 'inherit' };
const V2_ICON_LINK = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 2, borderRadius: 4,
  color: 'var(--accent-text)', textDecoration: 'none', lineHeight: 0, flexShrink: 0 };
// A card row that opens something: full width, the card's own rule above it.
const V2_ROW_BTN = { width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: 'none',
  borderTop: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
  fontSize: 12.5, color: 'var(--text)', minWidth: 0 };
const V2_CHEVRON = { width: 20, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  border: '1px solid var(--border)', borderLeft: 'none', borderRadius: '0 7px 7px 0', background: 'var(--surface)',
  color: 'var(--text-3)', cursor: 'pointer' };
const V2_MENU = { position: 'fixed', minWidth: 240, maxWidth: 360, padding: 4, background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 8, boxShadow: 'var(--shadow-lg)' };

// The same look as the classic property Row (110px label, 12px value); '—' when empty.
const KeyRow = ({ label, value, clip = true }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', minHeight: 30 }}>
    <span style={{ width: 110, fontSize: 11.5, color: 'var(--text-3)', flexShrink: 0 }}>{label}</span>
    <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text)', ...(clip ? { overflow: 'hidden', textOverflow: 'ellipsis' } : {}) }}>
      {value == null || value === '' || value === '—' ? <span style={{ color: 'var(--text-4)' }}>—</span> : value}
    </span>
  </div>
);

// A one-line fold toggle inside a card (chevron + label). No hooks.
const FoldRow = ({ label, open, onToggle }) => (
  <button type="button" aria-expanded={open} onClick={onToggle}
    style={{ ...V2_ROW_BTN, color: 'var(--text-3)', fontSize: 12, padding: '7px 14px' }}>
    <span aria-hidden="true" style={{ display: 'inline-flex', color: 'var(--text-4)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}>
      <window.Icon.ChevronR size={11} />
    </span>
    <span>{label}</span>
  </button>
);

// ── header controls ──────────────────────────────────────────────────────────
// A main button plus a chevron that lists every entry (a body portal at the
// menu z). items: [{ key, primary, sub, actions: [node] }].
const SplitMenu = ({ label, icon, iconOnly = false, onMain, items, title }) => {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);
  const close = React.useCallback(() => { setOpen(false); setPos(null); }, []);
  // Escape closes and hands focus back to the chevron, like RecordMenu.
  window.useEscapeKey(open, () => { close(); try { if (btnRef.current) btnRef.current.focus(); } catch (_) { /* detached */ } });
  React.useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, (window.innerWidth || 1280) - 368)) });
  }, [open]);
  React.useEffect(() => {
    if (!open) return undefined;
    const inside = (t) => (popRef.current && popRef.current.contains(t)) || (btnRef.current && btnRef.current.contains(t));
    const onDown = (e) => { if (!inside(e.target)) close(); };
    const onScroll = (e) => { if (!(popRef.current && popRef.current.contains(e.target))) close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);
  if (!items || !items.length) return null;
  return (
    <span data-split-menu={label} style={{ display: 'inline-flex', alignItems: 'center' }}>
      <window.Btn size="xs" variant="outline" icon={icon} title={title} onClick={onMain}
        style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
        {iconOnly ? null : label}
      </window.Btn>
      <button ref={btnRef} type="button" aria-haspopup="true" aria-expanded={open} aria-label={`${label} options`}
        title={`${label} options`} onClick={() => (open ? close() : setOpen(true))} style={V2_CHEVRON}>
        <window.Icon.ChevronD size={10} />
      </button>
      {open && pos && ReactDOM.createPortal(
        <div ref={popRef} role="menu" data-split-menu-list={label} style={{ ...V2_MENU, top: pos.top, left: pos.left, zIndex: core().Z.menu }}>
          {items.map((it) => (
            <div key={it.key} role="menuitem" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', minWidth: 0 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.primary}</span>
                {it.sub && <span style={{ display: 'block', ...V2_DIM }}>{it.sub}</span>}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>{it.actions}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </span>
  );
};

// Call ▾: the first dialable number; the list carries every number with Call,
// Text and Copy (CallButton asks first on do-not-call).
const CallMenu = ({ reach: r, dnc, name, iconOnly = false }) => {
  const first = r.dialable[0];
  if (!first) return null;
  const items = r.dialable.map((p) => ({
    key: p.key, primary: p.display, sub: p.label,
    actions: [
      <window.CallButton key="call" phone={p.display} doNotCall={dnc} name={name} text size={12} />,
      <window.CopyIconBtn key="copy" text={p.display} />,
    ],
  }));
  return (
    <SplitMenu label="Call" icon={<window.Icon.Phone size={12} />} iconOnly={iconOnly} title={`Call ${first.display}`}
      onMain={() => window.CallGuard.dial(first.tel, dnc, 'call', name)} items={items} />
  );
};
// Email ▾: the primary address; the list carries the aliases with mailto and Copy.
const EmailMenu = ({ reach: r }) => {
  const first = r.emails[0];
  if (!first) return null;
  const items = r.emails.map((e) => ({
    key: e.email, primary: e.email, sub: e.label,
    actions: [
      <a key="mail" href={e.href} style={V2_ICON_LINK} aria-label={`Email ${e.email}`} title={`Email ${e.email}`}><window.Icon.Mail size={12} /></a>,
      <window.CopyIconBtn key="copy" text={e.email} />,
    ],
  }));
  return (
    <SplitMenu label="Email" icon={<window.Icon.Mail size={12} />} title={`Email ${first.email}`}
      onMain={() => { window.location.href = first.href; }} items={items} />
  );
};
// One element in `primaries` (RecordHeader slices to 3): up to three controls
// within the shell's budget (bodyW / touch from useRecordShell).
const ContactHeaderControls = ({ contact, reach: r, dnc, canWrite, referralRole, merged, onLogReferral }) => {
  const shell = window.useRecordShell();
  const b = headerBudget(shell ? shell.bodyW : null, {
    touch: !!(shell && shell.touch), canWrite, referralRole, merged, phones: r.dialable.length, emails: r.emails.length,
  });
  return (
    <>
      {b.logReferral && window.NewReferralDrawer && (
        <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />}
          title={`Record a referral ${contact.name} made`} onClick={onLogReferral}>Log referral they made</window.Btn>
      )}
      {b.call && <CallMenu reach={r} dnc={dnc} name={contact.name} iconOnly={b.call === 'icon'} />}
      {b.email && <EmailMenu reach={r} />}
    </>
  );
};

// One amber line: the record was merged into another; the link opens the survivor.
const MergedBanner = ({ mergedIntoId }) => (
  <div data-contact-banner="merged" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-2)',
    padding: '4px 8px', borderRadius: 6, background: 'var(--amber-bg, var(--surface-2))', border: '1px solid var(--border)' }}>
    <window.Icon.Users size={12} />
    <button type="button" style={V2_LINK} onClick={() => { if (mergedIntoId && window.AtlasNav) window.AtlasNav.goToContact(mergedIntoId); }}>
      Merged · Open the kept contact →
    </button>
  </div>
);

// ── left column ──────────────────────────────────────────────────────────────
// Reach: every number (Call · Text · Copy), every address (mail · Copy),
// LinkedIn, the calling flags. Editing a number lives in All details, and so
// does adding one: Add phone / Add email show only while that row is empty.
const ReachCard = ({ contact, contactId, dnc, canWrite, onSave, detailsAvail }) => {
  const sum = window.useRecordSummary('contact', contactId);
  const raw = contact._raw || {};
  const r = reach(raw);
  const dcLocked = Boolean(raw.dealcloud_id);
  const flags = callFlags(sum.data && sum.data.calls);
  const preview = reachPreview(r);
  const openDetails = () => window.openRecordPanel('details', {}, { via: 'button' });
  return (
    <window.Section variant="rail" title="Reach" collapsible id="contact.reach" defaultOpen preview={preview}
      action={detailsAvail && canWrite ? (
        <window.Btn size="xs" variant="ghost" title="All details" icon={<window.Icon.Edit size={11} />} onClick={openDetails} />
      ) : null}>
      {r.phones.map((p, i) => (
        <KeyRow key={p.key} label={i === 0 ? 'Phone' : ''} value={p.dialable ? (
          <span style={V2_ROW}>
            <window.CallButton phone={p.display} doNotCall={dnc} name={contact.name} text size={12} />
            {p.label && <span style={V2_DIM}>{p.label}</span>}
            <window.CopyIconBtn text={p.display} />
          </span>
        ) : (
          <span data-phone-undialable="" style={{ ...V2_ROW, color: 'var(--text-4)' }}>
            <window.Icon.Phone size={11} />
            <s>{p.display}</s>
            <window.HelpTip text="Not dialable as written" />
          </span>
        )} />
      ))}
      {!r.phones.length && <KeyRow label="Phone" value={null} />}
      {r.emails.map((e, i) => (
        <KeyRow key={e.email} label={i === 0 ? 'Email' : ''} value={(
          <span style={V2_ROW}>
            <a href={e.href} style={{ ...V2_LINK, textDecoration: 'none' }}>{e.email}</a>
            {e.label && e.label !== 'primary' && <span style={V2_DIM}>{e.label}</span>}
            <window.CopyIconBtn text={e.email} />
          </span>
        )} />
      ))}
      {!r.emails.length && <KeyRow label="Email" value={null} />}
      <KeyRow label="LinkedIn" value={(
        <window.EditableCell type="url" value={raw.linkedin_url || ''} locked={dcLocked} editable={canWrite}
          onSave={(linkedin_url) => onSave({ linkedin_url })} />
      )} />
      {flags.length > 0 && (
        <KeyRow label="" value={(
          <span data-reach-flags="" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {flags.map((f) => <window.Pill key={f} hue="slate" sz="sm">{f}</window.Pill>)}
          </span>
        )} />
      )}
      {detailsAvail && canWrite && (!r.phones.length || !r.emails.length) && (
        <div style={{ padding: '4px 14px 10px', display: 'flex', gap: 12 }}>
          {!r.phones.length && <button type="button" style={V2_LINK} onClick={openDetails}>Add phone</button>}
          {!r.emails.length && <button type="button" style={V2_LINK} onClick={openDetails}>Add email</button>}
        </div>
      )}
    </window.Section>
  );
};

// Key facts: Owner · Type · the Company group (when linked). No hooks. Every
// cell takes editable={canWrite}, the reused rows included (a viewer reads).
const KeyFactsCard = ({ contact, company, users, typeOptions, canWrite, onSave, onSaveCompany, onOpenCompany, detailsAvail }) => {
  const raw = contact._raw || {};
  const dcLocked = Boolean(raw.dealcloud_id);
  const typeLabel = ((typeOptions || []).find((o) => o.value === raw.contact_type) || {}).label || raw.contact_type || '—';
  const preview = `${raw.contact_owner_name || '—'} · ${typeLabel}`;
  const co = company && company._raw ? company._raw : company || {};
  return (
    <window.Section variant="rail" title="Key facts" collapsible id="contact.key_facts" defaultOpen preview={preview}>
      <KeyRow label="Owner" value={(
        <span style={V2_ROW}>
          <window.EditableCell type="user" value={raw.contact_owner_id || null} users={users} locked={dcLocked} editable={canWrite}
            fallbackLabel={raw.contact_owner_name || null}
            onSave={(value) => window.saveOwnerWithScope({ kind: 'contact', field: 'contact_owner_id', value, users,
              companyName: raw.company_name || null, hasCompany: !!raw.company_id, save: onSave })} />
          {raw.owner_locked && <window.OwnerLockHint />}
        </span>
      )} />
      <KeyRow label="Type" value={(
        <window.EditableCell type="select" value={raw.contact_type || null} options={typeOptions} editable={canWrite}
          onSave={(contact_type) => onSave({ contact_type })} />
      )} />
      {company && (
        <>
          <div style={{ padding: '10px 14px 2px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Company</div>
          <KeyRow label="Company" clip={false} value={(
            <window.CompanyRowEditor contact={contact} company={company} editable={canWrite} onSave={onSave} onOpenCompany={onOpenCompany} />
          )} />
          <KeyRow label="Industry" value={co.industry_name ? <window.Pill hue="teal" sz="sm">{co.industry_name}</window.Pill> : null} />
          <window.LeadSourceFields raw={co} onSave={onSaveCompany} Row={KeyRow} eventLabel="Lead event" editable={canWrite} />
        </>
      )}
      {detailsAvail && (
        <div style={{ padding: '4px 14px 10px' }}>
          <button type="button" style={V2_LINK} onClick={() => window.openRecordPanel('details', {}, { via: 'button' })}>All details ›</button>
        </div>
      )}
    </window.Section>
  );
};

// ── right column cards ───────────────────────────────────────────────────────
const DealRow = ({ e, onOpen, dim = false }) => {
  const raw = e._raw || {};
  const shell = e.type === 'buyer_buyside';
  const name = shell ? `Pursuit of ${e.sellerEngagementName || e.targetName || e.name}` : e.name;
  const hue = (window.STAGE_DOT_HUE || {})[e.stage] || 'slate';
  const next = raw.next_action_date && window.fmtDate ? window.fmtDate(raw.next_action_date) : null;
  const size = e.size != null && window.fmtMoney ? window.fmtMoney(e.size) : null;
  return (
    <button type="button" data-deal-row={e.id} onClick={() => onOpen(e.id)} style={{ ...V2_ROW_BTN, opacity: dim ? 0.7 : 1 }}>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
        <span style={V2_ROW}>
          <window.Pill hue={hue} dot sz="sm">{e.stageName || e.stage}</window.Pill>
          {e.onHold && <window.Pill hue="amber" sz="sm">on hold</window.Pill>}
          {next && <span style={V2_DIM}>{next}</span>}
        </span>
      </span>
      {size && <span className="num" style={{ fontSize: 12, color: 'var(--text-2)', flexShrink: 0 }}>{size}</span>}
    </button>
  );
};

// Deals at <company>: live deals, then the exited/archived and pursuit folds.
const ContactDealsCard = ({ company, companyEngs, canWrite, onOpenDeal, onNew }) => {
  const [showMore, setShowMore] = React.useState({ done: false, shells: false });
  if (!company) return null;
  const d = dealsSplit(companyEngs);
  const loading = companyEngs === null;
  const title = `Deals at ${company.name} · ${loading ? '—' : d.count}`;
  const preview = loading ? '—' : `${d.live.length} live`;
  const doneLabel = `Exited · Archived · ${d.done.length}`;
  const shellLabel = `Deal pursuits · ${d.shells.length}`;
  return (
    <window.SummaryCard title={title} collapsible id="contact.deals" defaultOpen preview={preview}
      action={canWrite && window.NewEngagementDrawer ? (
        <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />} onClick={onNew}>New</window.Btn>
      ) : null}>
      {loading && <div style={{ padding: 14, color: 'var(--text-4)', fontSize: 12 }}>—</div>}
      {!loading && d.live.map((e) => <DealRow key={e.id} e={e} onOpen={onOpenDeal} />)}
      {!loading && !d.live.length && !d.done.length && <div style={{ padding: 14, color: 'var(--text-4)', fontSize: 12 }}>0</div>}
      {d.done.length > 0 && <FoldRow label={doneLabel} open={showMore.done} onToggle={() => setShowMore((s) => ({ ...s, done: !s.done }))} />}
      {showMore.done && d.done.map((e) => <DealRow key={e.id} e={e} onOpen={onOpenDeal} dim />)}
      {d.shells.length > 0 && <FoldRow label={shellLabel} open={showMore.shells} onToggle={() => setShowMore((s) => ({ ...s, shells: !s.shells }))} />}
      {showMore.shells && d.shells.map((e) => <DealRow key={e.id} e={e} onOpen={onOpenDeal} dim />)}
    </window.SummaryCard>
  );
};

// Outreach: status · channel · last campaign · last reply, the PSAC chip (or
// + PSAC) in the header; the row opens the Outreach history drawer.
const OutreachLine = ({ contactId, canWrite }) => {
  const sum = window.useRecordSummary('contact', contactId);
  const [busy, setBusy] = React.useState(false);
  const s = sum.data || {};
  const line = outreachLine(s.outreach);
  if (!line) return null;
  const addPsac = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await window.AtlasApi.addContactToPsac(contactId);
      window.pushToast({ type: 'success', title: 'Added to PSAC' });
      sum.refresh();
    } catch (err) {
      window.pushToast({ type: 'error', title: 'Add to PSAC failed', body: err.message });
    } finally { setBusy(false); }
  };
  let psac = null;
  if (line.psac === 'in') psac = <window.Pill hue="green" sz="sm">in PSAC</window.Pill>;
  else if (line.psac === 'out') psac = <window.Pill hue="slate" sz="sm">excluded from PSAC</window.Pill>;
  else if (canWrite) psac = <window.Btn size="xs" variant="outline" disabled={busy} onClick={addPsac}>+ PSAC</window.Btn>;
  const text = [line.channel, line.campaign, line.reply].filter(Boolean).join(' · ');
  const preview = [line.status, text].filter(Boolean).join(' · ') || '—';
  return (
    <window.SummaryCard title="Outreach" collapsible id="contact.outreach" defaultOpen preview={preview} action={psac}>
      <button type="button" data-card-row="outreach" onClick={() => window.openRecordPanel('outreach', {}, { via: 'button' })} style={V2_ROW_BTN}>
        {line.status && <window.Pill hue={line.hue} sz="sm">{line.status}</window.Pill>}
        {line.status || text
          ? <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
          : <span style={{ color: 'var(--text-4)', fontSize: 12 }}>—</span>}
      </button>
    </window.SummaryCard>
  );
};

// Programs: one line per program that applies; each opens the drawer at its tab.
const ProgramsCard = ({ contactId, roles }) => {
  const sum = window.useRecordSummary('contact', contactId);
  const [made, setMade] = React.useState(null);
  const maker = (roles || []).some((r) => MAKER_ROLES.includes(r));
  // The count of referrals this person made (GET /api/referrals?referral_source_contact_id);
  // re-read whenever the summary lands again (a logged referral refreshes it).
  React.useEffect(() => {
    if (!maker) return undefined;
    let alive = true;
    window.AtlasApi.listReferrals({ referral_source_contact_id: contactId, limit: 1 })
      .then((r) => { if (alive) setMade(r && r.total != null ? r.total : ((r && r.items) || []).length); })
      .catch(() => { if (alive) setMade(0); });
    return () => { alive = false; };
  }, [contactId, maker, sum.data]);
  // Before the first summary the lines would come from the roles alone ('GPP ·
  // no partner record' for a person who has one): a dimmed — until it lands.
  const loading = !sum.data;
  const s = sum.data || {};
  const lines = programLines(s.programs, roles, { made });
  if (!lines.length) return null;
  const preview = loading ? '—' : lines.map((l) => l.text).join(' · ');
  return (
    <window.SummaryCard title="Programs" collapsible id="contact.programs" defaultOpen preview={preview}>
      {loading && <div style={{ padding: 14, color: 'var(--text-4)', fontSize: 12 }}>—</div>}
      {!loading && lines.map((l) => (
        <button key={l.tab} type="button" data-program-line={l.tab} style={V2_ROW_BTN}
          onClick={() => window.openRecordPanel('programs', { tab: l.tab }, { via: 'button' })}>
          {l.text}
        </button>
      ))}
    </window.SummaryCard>
  );
};

// ── drawer bodies (PanelHost) ────────────────────────────────────────────────
// Every drawer body runs inside window.ContactDrawerScope (views/contact_detail.jsx):
// Escape in a nested input, an open typeahead list or a modal closes that one
// thing, never the drawer. No hooks.
const InDrawer = ({ children }) => {
  const Scope = window.ContactDrawerScope;
  return Scope ? <Scope.Provider value={true}>{children}</Scope.Provider> : children;
};

// All details: the classic properties panel, verbatim (DC lock, aliases + the
// 409 merge offer, Imported type, Background, ID, the merge row).
const ContactDetailsBody = ({ api }) => {
  const c = api.ctx || {};
  return (
    <InDrawer>
      <window.ContactPropertiesPanel contact={api.record} company={c.company} users={c.users} typeOptions={c.typeOptions}
        onSave={c.saveContact} onSaveCompany={c.saveCompanyLead} onOpenCompany={c.onOpenCompany} onRefresh={c.onRefresh} />
    </InDrawer>
  );
};

const PROGRAM_TAB_LABEL = { referral: 'Referred by', made: 'Referrals they made', events: 'Events', gpp: 'GPP', brand: 'Brand Builder' };
const ProgramsTabs = ({ tabs, tab, onTab }) => (
  <div role="tablist" style={{ display: 'flex', gap: 4, minWidth: 0, overflowX: 'auto' }}>
    {tabs.map((t) => (
      <button key={t} type="button" role="tab" aria-selected={t === tab} onClick={() => onTab(t)}
        style={{ border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
          fontSize: 12.5, fontWeight: t === tab ? 600 : 500,
          background: t === tab ? 'var(--accent-soft)' : 'transparent', color: t === tab ? 'var(--accent-text)' : 'var(--text-2)' }}>
        {PROGRAM_TAB_LABEL[t] || t}
      </button>
    ))}
  </div>
);

// The referrals this person made: name · company · date · owner · GPP. No hooks.
const MadeList = ({ rows, onOpen }) => {
  if (!rows) return <div style={{ padding: 14, color: 'var(--text-4)', fontSize: 12 }}>—</div>;
  const items = rows.items || [];
  if (!items.length) return <div style={{ padding: 14, color: 'var(--text-4)', fontSize: 12 }}>0</div>;
  return (
    <div data-made-list="" style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      {items.map((r, i) => (
        <button key={r.id} type="button" data-made-row={r.id} onClick={() => onOpen && onOpen(r.id)}
          style={{ ...V2_ROW_BTN, borderTop: i ? '1px solid var(--border)' : 'none' }}>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.full_name}</span>
            <span style={V2_DIM}>{[r.company_name, r.referral_owner_name].filter(Boolean).join(' · ')}</span>
          </span>
          {r.referral_is_gpp && <window.Pill hue="violet" sz="sm">GPP</window.Pill>}
          <span style={{ ...V2_DIM, flexShrink: 0 }}>{window.fmtReferralDay ? window.fmtReferralDay(r.referral_date) : (r.referral_date || '—')}</span>
        </button>
      ))}
    </div>
  );
};

// Programs: tabs for the programs that apply (plus the one asked for). The
// four classic panels stay mounted whenever the body is mounted (hidden tabs
// are display:none), so a ⋯ create request (openRecordPanel, then askProgram
// on the next frame) reaches a mounted useAskTick. cta (a writer, not a
// merged duplicate) gates the create buttons; readOnly={!cta} hides the
// panels' Edit / Remove / × too, so a viewer or a merged record reads.
const ContactProgramsBody = ({ api }) => {
  const c = api.ctx || {};
  const sum = window.useRecordSummary('contact', api.rec.id);
  const [made, setMade] = React.useState(null);
  const requested = api.params && api.params.tab;
  const contact = api.record;
  const roles = (contact && contact._raw && contact._raw.roles) || [];
  const merged = !!(contact && contact._raw && contact._raw.is_duplicate);
  const cta = !!api.canWrite && !merged;
  React.useEffect(() => {
    if (!api.active) return undefined;
    let alive = true;
    window.AtlasApi.listReferrals({ referral_source_contact_id: api.rec.id, limit: 500 })
      .then((r) => { if (alive) setMade(r || { items: [], total: 0 }); })
      .catch(() => { if (alive) setMade({ items: [], total: 0 }); });
    return () => { alive = false; };
  }, [api.active, api.rec.id, c.dataTick]);
  const tabs = programTabs(sum.data && sum.data.programs, roles, requested);
  const tab = tabs.includes(requested) ? requested : tabs[0] || null;
  const ask = c.programAsk || {};
  const show = (t) => ({ display: tab === t ? 'block' : 'none' });
  const body = (
    <div data-programs-body="" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {tabs.length
        ? <ProgramsTabs tabs={tabs} tab={tab} onTab={(t) => api.set({ tab: t }, { via: 'button' })} />
        : <div data-programs-empty="" style={{ padding: 14, color: 'var(--text-4)', fontSize: 12 }}>—</div>}
      <div data-program-tab="referral" style={show('referral')}>
        <window.ContactReferralPanel contact={contact} users={c.users} onSave={c.saveContact} setContactId={c.setContactId}
          setCompanyId={c.setCompanyId} cta={cta} readOnly={!cta} createTick={ask.referral || 0} />
      </div>
      <div data-program-tab="made" style={{ display: tab === 'made' ? 'flex' : 'none', flexDirection: 'column', gap: 10 }}>
        <MadeList rows={made} onOpen={c.setContactId} />
        {cta && window.NewReferralDrawer && (
          <span>
            <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />} onClick={c.onLogReferral}>Log referral they made</window.Btn>
          </span>
        )}
      </div>
      <div data-program-tab="events" style={show('events')}>
        <window.ContactLeadEventsPanel contact={contact} activeUser={c.activeUser} optional={false} cta={cta} readOnly={!cta} createTick={ask.event || 0}
          onPresence={(has) => c.onPresence && c.onPresence('events', has)} onChanged={c.onLeadEventChanged} />
      </div>
      <div data-program-tab="gpp" style={show('gpp')}>
        <window.ContactGppPanel contact={contact} users={c.users} setContactId={c.setContactId} setCompanyId={c.setCompanyId}
          optional={false} cta={cta} readOnly={!cta} createTick={ask.gpp || 0} onPresence={(has) => c.onPresence && c.onPresence('gpp', has)} allRefs />
      </div>
      <div data-program-tab="brand" style={show('brand')}>
        <window.ContactReferralPartnerPanel contact={contact} users={c.users} activeUser={c.activeUser} optional={false} cta={cta} readOnly={!cta}
          logReferral={false} createTick={ask.bb || 0} onPresence={(has) => c.onPresence && c.onPresence('bb', has)} />
      </div>
    </div>
  );
  return <InDrawer>{body}</InDrawer>;
};

// Outreach history: the classic panel, verbatim (status, PSAC + Add to PSAC,
// campaigns with steps, replies, cold calls, the status history).
const ContactOutreachBody = ({ api }) => (
  <window.ContactOutreachPanel contact={api.record} sectionId="contact.outreach_history" defaultOpen />
);

// ⋯ › Merge another contact into this one: the classic merge row, opened, in a
// drawer. Mounted only while open, so initialOpen lands on a fresh mount each time.
const ContactMergeDrawer = ({ open, contact, onClose, onMerged, zIndex }) => {
  if (!open) return null;
  return (
    <window.Drawer open title="Merge duplicate" width={440} widthKey="contact.merge" zIndex={zIndex} onClose={onClose}>
      <InDrawer>
        <window.ContactMergeRow contact={contact} initialOpen onMerged={async () => { if (onMerged) await onMerged(); onClose(); }} />
      </InDrawer>
    </window.Drawer>
  );
};

// ── the page ─────────────────────────────────────────────────────────────────
const ContactPageV2 = ({ contactId, contact, company, companyEngs, users, activeUser, typeOptions, back,
  dnc, ctas, roles, presence, onPresence, menuItems, askProgram, programAsk,
  feed, onSave, onSaveCompany, onReload, setContact, setCompany, setCompanyEngs, setDealId, setCompanyId, panel, drawers }) => {
  // v2-only hooks (never in ContactDetail). The page is keyed by the layout flag.
  const sum = window.useRecordSummary('contact', contactId);                 // last touch, the DNC flag, the PSAC item, presence
  const draftOpen = window.useRecordDraftOpen('contact', contactId);
  const logDot = window.useRecordDraftFlag('contact', contactId);
  const pp = window.usePanelParam({ kind: 'contact', id: contactId });     // a pop-out open → z70 on the page's drawers
  const [composeRequest, setComposeRequest] = React.useState(null);
  const [mergeOpen, setMergeOpen] = React.useState(false);
  const [newDeal, setNewDeal] = React.useState(null);                      // { stages } while the New engagement drawer is open
  const [dataTick, setDataTick] = React.useState(0);
  // The Programs drawer re-reads the referrals this person made after one is logged.
  React.useEffect(() => {
    const bump = () => setDataTick((n) => n + 1);
    window.addEventListener('atlas-data-changed', bump);
    return () => window.removeEventListener('atlas-data-changed', bump);
  }, []);
  // Seed the classic `presence` from the summary, so the ⋯ create items are right
  // before the Programs drawer (which hosts the panels that report it) ever opens.
  // Each seed is what its panel reports once mounted (events: rows > 0; gpp /
  // bb: a row exists — a soft-removed Brand Builder row included: the panel's
  // own Add to list is the re-activate path, never a second ⋯ item).
  const programs = sum.data && sum.data.programs;
  React.useEffect(() => {
    if (!programs || !onPresence) return;
    onPresence('events', !!(programs.events && programs.events.count > 0));
    onPresence('gpp', !!programs.gpp);
    onPresence('bb', !!programs.brand_builder);
  }, [programs]);   // eslint-disable-line react-hooks/exhaustive-deps

  const cr = contact._raw || {};
  const canWrite = window.recordCanWrite();
  const merged = !!(cr.is_duplicate && cr.merged_into_id);
  const s = sum.data || {};
  const dncNow = dnc || !!(s.calls && s.calls.do_not_call);
  const r = reach(cr);
  // The page's own drawers render in the page tree; over an open reference drawer (z50, body portal) or a
  // workspace (z55) they stack (Z.stacked).
  const z = pp.name ? core().Z.stacked : undefined;
  const avail = (n) => window.recordPanelAvailable(n, 'v2');
  const detailsAvail = avail('details');
  const openPanel = (n, p) => window.openRecordPanel(n, p || {}, { via: 'button' });
  const onLog = (kind) => setComposeRequest({ kind, n: Date.now() });
  const onLogReferral = () => drawers.setLoggingReferral(true);
  const onOpenCompany = () => { if (company && setCompanyId) setCompanyId(company.id); };
  const K = window.RecordNotesKit;
  const attach = { contact_id: contactId, company_id: cr.company_id || null };
  const links = { contact: { id: contactId, name: contact.name },
    company: cr.company_id ? { id: cr.company_id, name: cr.company_name || 'Company' } : null };
  const onSaved = (res) => {
    if (res.type === 'task') { window.dispatchEvent(new CustomEvent('task-created')); return; }
    if (feed.reload) feed.reload();
    if (res.stamped && attach.company_id) {
      window.AtlasApi.getCompany(attach.company_id).then((raw) => setCompany(window.AtlasData.__adaptCompany(raw))).catch(() => {});
    }
  };
  // ⋯: a program item opens the drawer at its tab, then bumps the panel's createTick
  // on the next frame (the body mounts in the same commit as the URL write; its
  // useAskTick seeds at mount and opens on the change that follows).
  const openProgram = (k) => {
    const tab = PROGRAM_TAB[k];
    if (openPanel('programs', { tab })) requestAnimationFrame(() => askProgram(k)); else askProgram(k);
  };
  const outreachShown = !!outreachLine(s.outreach);
  const programsShown = programLines(s.programs, roles, {}).length > 0;   // = the Programs card's own gate
  // The folded strip's Deals icon opens the card's first row (live, then exited/archived, then a pursuit).
  const dSplit = dealsSplit(companyEngs);
  const firstDeal = dSplit.live[0] || dSplit.done[0] || dSplit.shells[0] || null;
  const addPsac = () => window.AtlasApi.addContactToPsac(contactId)
    .then(() => { window.pushToast({ type: 'success', title: 'Added to PSAC' }); sum.refresh(); })
    .catch((e) => window.pushToast({ type: 'error', title: 'Add to PSAC failed', body: e.message }));
  const v2Menu = [
    avail('history') && { key: 'history', label: 'History', onSelect: () => openPanel('history') },
    ctas && { key: 'merge', label: 'Merge another contact into this one…', onSelect: () => setMergeOpen(true) },
    { divider: true },
    ...(menuItems || []).map((it) => (it && it.key !== 'logref' ? { ...it, onSelect: () => openProgram(it.key) } : it)),
    ctas && !!sum.data && !outreachShown && { key: 'psac', label: 'Add to PSAC', onSelect: addPsac },
  ];
  const header = {
    crumb: { label: 'People', onClick: back },
    logo: <window.Avatar name={contact.name} size={32} color="oklch(0.50 0.13 295)" />,
    title: <h1 style={V2_H1}>{contact.name}</h1>,
    pills: [
      <window.RoleChips key="roles" roles={cr.roles} details={roleTips(cr, window.contactRoleDetails ? window.contactRoleDetails(cr) : undefined)} />,
      cr.is_decision_maker && <window.Pill key="dm" hue="green" sz="sm">decision-maker</window.Pill>,
      dncNow && (avail('calls')
        ? <button key="dnc" type="button" data-dnc-pill="" style={V2_PILL_BTN} onClick={() => openPanel('calls')}><window.Pill hue="rose" sz="sm" title="Do not call">DNC</window.Pill></button>
        : <span key="dnc" data-dnc-pill=""><window.Pill hue="rose" sz="sm" title="Do not call">DNC</window.Pill></span>),
    ],
    terminal: merged ? <window.Pill hue="amber" sz="sm">Merged</window.Pill> : null,
    primaries: [
      <ContactHeaderControls key="controls" contact={contact} reach={r} dnc={dncNow} canWrite={canWrite}
        referralRole={roles.some((x) => MAKER_ROLES.includes(x))} merged={merged} onLogReferral={onLogReferral} />,
    ],
    log: <window.LogButton onPick={onLog} disabled={!activeUser} dot={logDot} kinds={['note', 'call', 'email', 'meeting', 'task']} />,
    menu: <window.RecordMenu items={v2Menu} />,
    meta: cr.title || canWrite ? (
      <span data-contact-title="" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        <window.Icon.Tag size={12} />
        <window.EditableCell type="text" value={cr.title || ''} locked={Boolean(cr.dealcloud_id)} editable={canWrite} onSave={(title) => onSave({ title })} />
      </span>
    ) : null,
    lastTouch: sum.data ? (s.last_touch || null) : undefined,                 // A2: hidden until the first summary, dimmed — when null
    banner: merged ? <MergedBanner mergedIntoId={cr.merged_into_id} /> : null,
  };
  const left = (
    <>
      <ReachCard contact={contact} contactId={contactId} dnc={dncNow} canWrite={canWrite} onSave={onSave} detailsAvail={detailsAvail} />
      <KeyFactsCard contact={contact} company={company} users={users} typeOptions={typeOptions} canWrite={canWrite}
        onSave={onSave} onSaveCompany={onSaveCompany} onOpenCompany={onOpenCompany} detailsAvail={detailsAvail} />
      <window.ContactTagsPanel contactId={contactId} activeUser={canWrite ? activeUser : null} />
    </>
  );
  const composer = {
    open: draftOpen && canWrite,
    element: canWrite ? (
      <window.RecordComposer rec={{ kind: 'contact', id: contactId }} attach={attach} engagementPicker activeUser={activeUser} members={users}
        kinds={K.composerKindsFor(false)} presentation="inline" taskLinks={links} onSaved={onSaved} />
    ) : null,
    title: 'Log',
  };
  const act = <window.ContactOpenTasks contact={contact} contactId={contactId} users={users} activeUser={canWrite ? activeUser : null} showEmpty={canWrite} />;
  const feedEl = (
    <window.RecordNotes kind="contact" recordId={contactId} sectionId="contact.notes" composerSlot="external" feed={feed} attach={attach}
      activeUser={canWrite ? activeUser : null} members={users} engagementPicker label="Notes" taskLinks={links} composeRequest={composeRequest} />
  );
  const sellerCo = !!(cr.company_id && window.SellerBriefSection && window.isSellerCompany(company, null, [contact]));
  const onNewDeal = () => window.AtlasApi.listStages()
    .then((st) => setNewDeal({ stages: Array.isArray(st) ? st : [] }))
    .catch(() => setNewDeal({ stages: [] }));
  const right = (
    <>
      <ContactDealsCard company={company} companyEngs={companyEngs} canWrite={canWrite} onOpenDeal={(id) => setDealId(id)} onNew={onNewDeal} />
      <OutreachLine contactId={contactId} canWrite={canWrite && ctas} />
      {window.WebsiteVisitsCard && <window.WebsiteVisitsCard contactId={contactId} />}
      <ProgramsCard contactId={contactId} roles={roles} />
      <window.DialerCallsPanel contactId={contactId} />
      {sellerCo && <window.ContactCompanyCard contactId={contactId} companyId={cr.company_id} companyName={cr.company_name || null} variant="rail" />}
    </>
  );
  const cardIds = ['contact.deals', 'contact.outreach', 'contact.programs', 'contact.seller_brief', 'contact.company'];
  const rails = {
    left: detailsAvail ? [
      { key: 'reach', icon: <window.Icon.Phone size={14} />, label: 'Reach', onOpen: () => openPanel('details') },
      { key: 'key_facts', icon: <window.Icon.Tag size={14} />, label: 'Key facts', onOpen: () => openPanel('details') },
    ] : [],
    right: [
      company && firstDeal && { key: 'deals', icon: <window.Icon.Pipeline size={14} />, label: 'Deals', onOpen: () => setDealId(firstDeal.id) },
      outreachShown && { key: 'outreach', icon: <window.Icon.Mail size={14} />, label: 'Outreach', onOpen: () => openPanel('outreach') },
      programsShown && { key: 'programs', icon: <window.Icon.Users size={14} />, label: 'Programs', onOpen: () => openPanel('programs') },
      avail('calls') && { key: 'calls', icon: <window.Icon.Phone size={14} />, label: 'Calls', onOpen: () => openPanel('calls') },
      sellerCo && { key: 'company', icon: <window.Icon.Building size={14} />, label: 'Company', onOpen: () => openPanel('intel', { focus: 'facts' }) },
    ].filter(Boolean),
  };
  const panelProp = {
    record: contact, facts: panel.facts, variant: 'person',
    ctx: {
      ...panel.ctx, saveContact: onSave, saveCompanyLead: onSaveCompany, users, typeOptions, activeUser, onOpenCompany, onRefresh: onReload,
      companyId: cr.company_id || null,
      setContactId: (id) => { if (id && id !== contactId && window.AtlasNav) window.AtlasNav.goToContact(id); },
      setCompanyId, programAsk, onPresence, onLogReferral, dataTick,
      onLeadEventChanged: async () => {
        const fresh = await window.AtlasApi.getContact(contactId).catch(() => null);
        if (fresh) setContact(window.AtlasData.__adaptContact(fresh, {}));
        if (fresh && fresh.company_id) {
          const co = await window.AtlasApi.getCompany(fresh.company_id).catch(() => null);
          if (co) setCompany(window.AtlasData.__adaptCompany(co));
        }
      },
    },
  };
  return (
    <>
      <window.RecordPage kind="contact" id={contactId} header={header} left={left} act={act} feed={feedEl} right={right}
        cardIds={cardIds} rails={rails} composer={composer} panel={panelProp} onNoteChange={feed.reload} />
      {drawers.loggingReferral && window.NewReferralDrawer && (
        <window.NewReferralDrawer users={users} activeUser={activeUser} zIndex={z}
          initialSource={{ id: cr.id || contactId, full_name: cr.full_name || contact.name, company_id: cr.company_id || null, company_name: cr.company_name || null }}
          initialIsGpp={roles.includes('gpp') || presence.gpp === true}
          onClose={() => drawers.setLoggingReferral(false)}
          onCreated={() => { drawers.setLoggingReferral(false); window.dispatchEvent(new CustomEvent('atlas-data-changed')); }} />
      )}
      {newDeal && window.NewEngagementDrawer && company && (
        <window.NewEngagementDrawer stages={newDeal.stages} activeUser={activeUser} zIndex={z} initialCompany={{ id: company.id, name: company.name }}
          onClose={() => setNewDeal(null)}
          onCreated={(eng) => {
            setNewDeal(null);
            if (setCompanyEngs) setCompanyEngs((prev) => [window.AtlasData.__adaptEngagement(eng), ...(prev || [])]);
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
            if (eng && eng.id && setDealId) setDealId(eng.id);
          }} />
      )}
      <ContactMergeDrawer open={mergeOpen} contact={contact} zIndex={z} onClose={() => setMergeOpen(false)} onMerged={onReload} />
    </>
  );
};

Object.assign(window, { ContactPageV2, ContactDetailsBody, ContactProgramsBody, ContactOutreachBody });
